<template>
  <div class="custom-audio-manager">
    <div class="custom-audio-header">
      <h3 class="custom-audio-title">
        Custom Audio Manager
        <span
          class="info-icon"
          @mouseenter="showTooltip = true"
          @mouseleave="showTooltip = false"
        >
          ℹ️
          <transition name="fade">
            <div v-if="showTooltip" class="tooltip">
              Uploading custom audio will replace the default background music for each section type. For best results, use loopable audio files for the Main section.
            </div>
          </transition>
        </span>
      </h3>
      <button class="toggle-btn" @click="isExpanded = !isExpanded">
        {{ isExpanded ? '▲' : '▼' }}
      </button>
    </div>

    <div v-if="isExpanded" class="custom-audio-content">
      <div class="section-audio-grid">
        <div v-for="(section, key) in sectionTypes" :key="key" class="section-audio-item">
          <div class="section-info">
            <span class="section-icon">{{ section.icon }}</span>
            <span class="section-name">{{ section.label }}</span>
          </div>

          <div class="audio-controls">
            <div v-if="audioMappings[key]" class="audio-preview">
              <div class="audio-file-info">
                <span class="file-name">{{ getFileName(audioMappings[key]) }}</span>
                <button class="play-btn" @click="previewAudio(key)" title="Preview">▶</button>
              </div>
              <button class="remove-btn" @click="removeAudio(key)">Remove</button>
            </div>
            <div v-else class="audio-upload">
              <label class="upload-btn" :for="`audio-upload-${key}`">
                Upload Audio
              </label>
              <input
                :id="`audio-upload-${key}`"
                type="file"
                accept="audio/mpeg,audio/mp3"
                @change="uploadAudio($event, key)"
                class="hidden-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="isUploading" class="upload-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: `${uploadProgress}%` }"></div>
        </div>
        <div class="progress-text">Uploading... {{ uploadProgress }}%</div>
      </div>

      <div v-if="previewingAudio" class="audio-preview-player">
        <audio ref="audioPreview" controls :src="previewAudioSrc"></audio>
        <button class="close-preview" @click="stopPreview">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/api";
import { mapActions } from 'vuex';

export default {
  name: 'CustomAudioManager',
  props: {
    messageId: {
      type: [Number, String],
      required: true
    },
    isPro: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showTooltip: false,
      isExpanded: false,
      audioMappings: {},
      isUploading: false,
      uploadProgress: 0,
      previewingAudio: false,
      previewAudioSrc: null,
      previewKey: null,
      sectionTypes: {
        preview: { icon: '🔍', label: 'Preview' },
        intro: { icon: '🎙️', label: 'Intro' },
        main: { icon: '📝', label: 'Main Content' },
        break: { icon: '💰', label: 'Sponsor Break' },
        last: { icon: '🏁', label: 'Closing' },
        discussion: { icon: '💬', label: 'Discussion' },
        insight: { icon: '💡', label: 'Insight' },
        resonance: { icon: '🔄', label: 'Resonance' },
        evolution: { icon: '🌱', label: 'Evolution' },
        portal: { icon: '🚪', label: 'Portal' },
        custom: { icon: '🛠️', label: 'Custom' }
      }
    }
  },
  created() {
    this.fetchAudioMappings();
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),
    async fetchAudioMappings() {
      try {
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token
          ? `/user_customization/messages/${this.messageId}/audio-mappings`
          : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/audio-mappings`;

        const response = await API.get(url);
        this.audioMappings = response.data;
      } catch (error) {
        console.error('Error fetching audio mappings:', error);
      }
    },

    async uploadAudio(event, sectionType) {
      if (!this.isPro) {
        this.showTempMessageFn({
          message: "Custom audio is only available to Pro users",
          type: "info",
          timeout: 3000
        });
        return;
      }

      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== 'audio/mpeg' && file.type !== 'audio/mp3') {
        this.showTempMessageFn({
          message: "Only MP3 files are supported",
          type: "error",
          timeout: 3000
        });
        return;
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section_type', sectionType);

      this.isUploading = true;
      this.uploadProgress = 0;

      try {
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token
          ? `/user_customization/messages/${this.messageId}/audio-mappings`
          : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/audio-mappings`;

        const response = await API.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          }
        });

        // Add the new mapping to our local state
        this.$set(this.audioMappings, sectionType, response.data.url);

        this.showTempMessageFn({
          message: "Audio uploaded successfully",
          type: "success",
          timeout: 3000
        });

        // Emit event to parent component
        this.$emit('audio-mapping-changed', {
          sectionType,
          url: response.data.url
        });
      } catch (error) {
        console.error('Error uploading audio:', error);
        this.showTempMessageFn({
          message: "Error uploading audio file",
          type: "error",
          timeout: 3000
        });
      } finally {
        this.isUploading = false;
        // Reset file input
        event.target.value = null;
      }
    },

    async removeAudio(sectionType) {
      try {
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token
          ? `/user_customization/messages/${this.messageId}/audio-mappings/${sectionType}`
          : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/audio-mappings/${sectionType}`;

        await API.delete(url);

        // Remove from local state
        this.$delete(this.audioMappings, sectionType);

        this.showTempMessageFn({
          message: "Audio mapping removed",
          type: "info",
          timeout: 3000
        });

        // Emit event to parent component
        this.$emit('audio-mapping-removed', {
          sectionType
        });
      } catch (error) {
        console.error('Error removing audio mapping:', error);
      }
    },

    previewAudio(sectionType) {
      if (this.audioMappings[sectionType]) {
        this.previewAudioSrc = this.audioMappings[sectionType];
        this.previewKey = sectionType;
        this.previewingAudio = true;
        this.$nextTick(() => {
          if (this.$refs.audioPreview) {
            this.$refs.audioPreview.play();
          }
        });
      }
    },

    stopPreview() {
      if (this.$refs.audioPreview) {
        this.$refs.audioPreview.pause();
      }
      this.previewingAudio = false;
      this.previewAudioSrc = null;
      this.previewKey = null;
    },

    getFileName(url) {
      if (!url) return '';
      const parts = url.split('/');
      let filename = parts[parts.length - 1];

      // Truncate long filenames
      if (filename.length > 25) {
        filename = filename.substring(0, 22) + '...';
      }

      return filename;
    }
  }
}
</script>

<style scoped>
.custom-audio-manager {
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.theme--dark .custom-audio-manager {
  background-color: #1a202c;
}

.custom-audio-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.custom-audio-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .custom-audio-title {
  color: #e2e8f0;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

.custom-audio-content {
  margin-top: 0.75rem;
}

.section-audio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.5rem;
}

.section-audio-item {
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
}

.theme--dark .section-audio-item {
  border-color: #2d3748;
}

.section-info {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.section-icon {
  margin-right: 0.5rem;
}

.section-name {
  font-size: 0.875rem;
  font-weight: 500;
}

.audio-controls {
  margin-top: auto;
}

.audio-upload {
  display: flex;
  justify-content: center;
}

.upload-btn {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  background-color: #ebf8ff;
  color: #3182ce;
  border: 1px solid #bee3f8;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.upload-btn:hover {
  background-color: #bee3f8;
}

.theme--dark .upload-btn {
  background-color: #2c5282;
  border-color: #2b4c74;
  color: #e2e8f0;
}

.theme--dark .upload-btn:hover {
  background-color: #2b6cb0;
}

.hidden-input {
  display: none;
}

.audio-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.audio-file-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.file-name {
  font-size: 0.75rem;
  color: #718096;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.play-btn {
  background: none;
  border: none;
  color: #3182ce;
  cursor: pointer;
  padding: 0.125rem 0.25rem;
  font-size: 0.75rem;
}

.remove-btn {
  width: 100%;
  padding: 0.25rem;
  background-color: #fed7d7;
  color: #e53e3e;
  border: 1px solid #feb2b2;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.remove-btn:hover {
  background-color: #feb2b2;
}

.theme--dark .remove-btn {
  background-color: #742a2a;
  border-color: #63171b;
  color: #fc8181;
}

.theme--dark .remove-btn:hover {
  background-color: #63171b;
}

.upload-progress {
  margin-top: 1rem;
}

.progress-bar {
  width: 100%;
  height: 0.5rem;
  background-color: #e2e8f0;
  border-radius: 0.25rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #4299e1;
  transition: width 0.2s ease;
}

.progress-text {
  text-align: center;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  color: #718096;
}

.audio-preview-player {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f7fafc;
  border-radius: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.theme--dark .audio-preview-player {
  background-color: #2d3748;
}

.audio-preview-player audio {
  width: 100%;
}

.close-preview {
  align-self: flex-end;
  padding: 0.25rem 0.5rem;
  background-color: #e2e8f0;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
}

.theme--dark .close-preview {
  background-color: #4a5568;
  color: #e2e8f0;
}

@media (max-width: 768px) {
  .section-audio-grid {
    grid-template-columns: 1fr;
  }

  .custom-audio-content {
    max-height: 300px;
    overflow-y: auto;
  }
}

/* New tooltip styles */
.info-icon {
  position: relative;
  cursor: help;
  margin-left: 8px;
  display: inline-block;
  font-size: 16px;
  line-height: 1;
}

.tooltip {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  transform: translateX(-50%);
  background-color: #333;
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.4;
  width: 300px;
  max-width: 90vw;
  white-space: normal;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Tooltip arrow */
.tooltip::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent transparent #333 transparent;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Optionally, add responsive positioning for the tooltip */
@media (max-width: 768px) {
  .tooltip {
    position: fixed;
    top: auto;
    bottom: 20px;
    left: 10px;
    right: 10px;
    transform: none;
    max-width: none;
  }

  .tooltip::before {
    display: none;
  }
}
</style>
